/**
 * The five cells of the mobile bar, per session
 * (SPEC_NAVEGACION_MOVIL, "Estados por sesión").
 *
 *   Anónimo:  Explorar · FixyBees · [Dany] · Trabajos  · Menú
 *   Cliente:  Trabajos · FixyBees · [Dany] · Mensajes  · Menú
 *   FixyBee:  Trabajos · Cotizaciones · [Dany] · Mensajes · Menú
 *
 * Every destination is a route that already exists; T12-M1 adds none. The two
 * columns named "Trabajos" in the table are three different pages, because the
 * word means a different errand to each session:
 *
 *   - anonymous → `/open-jobs`, the public showcase, the only jobs list a
 *     visitor without an account is allowed to read;
 *   - client    → `/client/jobs`, the jobs they posted;
 *   - FixyBee   → `/provider/browse`, the board they quote from.
 *
 * "Explorar" is the home, which is where the search and Dany's hero live — the
 * mockup's screen 01 is that page. `FixyBees` is the public directory for both
 * sessions that have it.
 *
 * `Mensajes` does not exist for an anonymous visitor, so the fourth cell is the
 * showcase instead. The bar is always five cells; the spec forbids a sixth.
 */

export const MENU_TAB = "menu";

/**
 * @param {object} opts
 * @param {boolean} opts.isLoggedIn
 * @param {string|undefined} opts.role  `client` | `provider` | `admin`
 * @returns {Array<{key: string, to: string, icon: string, labelKey: string,
 *                  badge?: boolean}>} four tabs; the Menu cell is the fifth and
 *   is a button, not a route, so it is appended by the bar itself.
 */
export function tabsForSession({ isLoggedIn, role }) {
  if (isLoggedIn && role === "provider") {
    return [
      { key: "jobs", to: "/provider/browse", icon: "briefcase", labelKey: "nav.tabs.jobs" },
      { key: "quotes", to: "/provider/quotes", icon: "file-text", labelKey: "nav.tabs.quotes" },
      { key: "messages", to: "/provider/messages", icon: "message-square", labelKey: "dashLayout.messages", badge: true },
    ];
  }
  if (isLoggedIn && role === "client") {
    return [
      { key: "jobs", to: "/client/jobs", icon: "briefcase", labelKey: "nav.tabs.jobs" },
      { key: "fixybees", to: "/providers", icon: "hard-hat", labelKey: "nav.tabs.fixybees" },
      { key: "messages", to: "/client/messages", icon: "message-square", labelKey: "dashLayout.messages", badge: true },
    ];
  }
  // Anonymous, and anyone signed in without a role yet (onboarding has not run,
  // so none of the namespaced routes would let them through).
  return [
    { key: "explore", to: "/", icon: "search", labelKey: "nav.tabs.explore" },
    { key: "fixybees", to: "/providers", icon: "hard-hat", labelKey: "nav.tabs.fixybees" },
    { key: "jobs", to: "/open-jobs", icon: "briefcase", labelKey: "nav.tabs.jobs" },
  ];
}

/**
 * Which tab the current route lights up.
 *
 * Two rules beyond "the path starts with the tab's route":
 *   - `/` is only ever the Explore tab when it is exactly `/`, or every route
 *     would match it;
 *   - anywhere inside `/client/dashboard`, `/provider/dashboard` or their
 *     settings-style children, the active tab is **Menú** — that is where the
 *     Panel is reached from, and the spec says the Panel keeps Menú lit in the
 *     index and in every section.
 */
const PANEL_PREFIXES = [
  "/panel",
  "/client/dashboard",
  "/client/settings",
  "/provider/dashboard",
  "/provider/settings",
  "/provider/billing",
  "/provider/verification",
  "/provider/landing-editor",
  "/provider/requests",
  "/provider/requests-for-you",
];

export function activeTabKey(pathname, tabs, fromPanel = false) {
  const path = String(pathname || "/");
  // Inside the Panel — its index, or any section of it — the lit cell is Menú
  // (SPEC_NAVEGACION_MOVIL, "Panel"). The two sections that are ALSO tabs only
  // count as "inside the Panel" when the Panel is where we came from; see
  // panelSections.js for why both readings are right.
  if (PANEL_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return MENU_TAB;
  if (fromPanel && tabs.some((t) => t.to === path)) return MENU_TAB;
  // Longest route first, so `/provider/requests-for-you` cannot be shadowed by
  // a shorter sibling that happens to be a prefix of it.
  const byLength = [...tabs].sort((a, b) => b.to.length - a.to.length);
  for (const tab of byLength) {
    if (tab.to === "/") {
      if (path === "/") return tab.key;
      continue;
    }
    if (path === tab.to || path.startsWith(`${tab.to}/`)) return tab.key;
  }
  return null;
}

export default tabsForSession;
